import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type { SystemGroupCreateRequest, SystemGroupUpdateRequest } from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";

const useCreateGroup = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: SystemGroupCreateRequest) => api.postSystemGroupsV1(payload),
    onSuccess: () => {
      showFlashToast({
        variant: "success",
        message: t("page.platform-groups.create.success"),
      });
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PLATFORM_GROUPS,
      });
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("page.platform-groups.create.error"),
      });
    },
  });
};

const useUpdateGroup = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: SystemGroupUpdateRequest }) =>
      api.putSystemGroupsV1(id, payload),
    onSuccess: () => {
      showFlashToast({
        variant: "success",
        message: t("page.platform-groups.edit.success"),
      });
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PLATFORM_GROUPS,
      });
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("page.platform-groups.edit.error"),
      });
    },
  });
};

const useDeleteGroup = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (groupId: string) => api.deleteSystemGroupsByIdV1(groupId),
    onSuccess: () => {
      showFlashToast({
        variant: "success",
        message: t("page.platform-groups.delete.success"),
      });
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PLATFORM_GROUPS_LIST(),
      });
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("page.platform-groups.delete.error"),
      });
    },
  });
};

export const platformGroupMutations = {
  useCreateGroup,
  useUpdateGroup,
  useDeleteGroup,
};
